import { Collapse, Row, Col, Tag, Table, message, Switch, Typography, Space } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  ModalItem,
  ButtonItem,
  InputNumberItem,
} from "../../../../Components/Items";
import moment from "moment";

const { Panel } = Collapse;

// Modal compartido por los tres puntos de entrada del vinculo factura <-> entregas:
// RecibirFactura, RecibirFacturaMultiple y FacturasOrdenDeCompra (edicion posterior).
//
// En los dos primeros NO habla con el servidor al guardar: devuelve la seleccion por
// onConfirm y el padre la manda dentro del submit de la factura. En el tercero recibe
// facturaId y guarda el mismo contra relacionarEntregasFactura.
//
// La seleccion vive en un mapa plano con clave `${origen}|${recepcionId}|${insumoOrdenId}`
// en vez de un arreglo: asi cada renglon tiene identidad propia y no hay que mutar
// arreglos en su lugar para que React lo note.
class RelacionarEntregasModal extends Component {
  state = {
    loading: false,
    entregas: [],
    seleccion: {},
    soloDeEstaFactura: true,
  };

  componentDidUpdate(prevProps) {
    if (this.props.visible !== prevProps.visible && this.props.visible)
      this.consultarTodo();
  }

  clave(origen, recepcionId, insumoOrdenId) {
    return origen + "|" + recepcionId + "|" + insumoOrdenId;
  }

  async consultarTodo() {
    const ordenesDeCompraIds = (this.props.ordenesDeCompraIds || []).filter(Boolean);
    if (ordenesDeCompraIds.length === 0) {
      this.setState({ entregas: [], seleccion: {} });
      return;
    }
    this.setState({ loading: true });
    await axios
      .post(
        "ordenesdecompra/entregasFacturablesOrdenDeCompra",
        {
          empresaId: this.props.empresaId,
          ordenesDeCompraIds,
          facturaId: this.props.facturaId,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          // La seleccion que ya trae el padre manda sobre la del servidor: si el
          // usuario abrio el modal, ligo cosas y lo volvio a abrir sin guardar la
          // factura, no queremos perderle lo capturado.
          const origen =
            Object.keys(this.props.seleccionInicial || {}).length > 0
              ? Object.values(this.props.seleccionInicial)
              : res.data.relacionesActuales || [];
          const seleccion = {};
          origen.forEach((valueRelacion) => {
            seleccion[
              this.clave(
                valueRelacion.origen,
                valueRelacion.recepcionId,
                valueRelacion.insumoOrdenId
              )
            ] = {
              origen: valueRelacion.origen,
              recepcionId: valueRelacion.recepcionId,
              insumoOrdenId: valueRelacion.insumoOrdenId,
              cantidad: valueRelacion.cantidad,
            };
          });
          this.setState({ entregas: res.data.entregas || [], seleccion });
        } else {
          message.error(res.data.message || "No se pudieron consultar las entregas");
        }
      })
      .catch(() => message.error("Error al consultar las entregas"));
    this.setState({ loading: false });
  }

  // Renglones de la factura, indexados por insumoOrdenId, para el resumen del pie.
  renglonesPorId() {
    const mapa = {};
    (this.props.renglonesFactura || []).forEach((valueRenglon) => {
      mapa[valueRenglon.insumoOrdenId?.toString()] = valueRenglon;
    });
    return mapa;
  }

  // Entregas visibles: por defecto solo las que tocan algun renglon que esta factura
  // realmente esta facturando.
  entregasVisibles() {
    if (!this.state.soloDeEstaFactura) return this.state.entregas;
    const renglones = this.renglonesPorId();
    return this.state.entregas
      .map((valueEntrega) => ({
        ...valueEntrega,
        insumos: valueEntrega.insumos.filter(
          (valueInsumo) => renglones[valueInsumo.insumoOrdenId?.toString()]
        ),
      }))
      .filter((valueEntrega) => valueEntrega.insumos.length > 0);
  }

  // Lo que falta por amparar de un renglon, contando lo ya ligado en OTRAS entregas
  // dentro de esta misma captura.
  pendientePorAmparar(insumoOrdenId, claveExcluida) {
    const renglon = this.renglonesPorId()[insumoOrdenId?.toString()];
    if (!renglon) return 0;
    const yaLigado = Object.entries(this.state.seleccion).reduce(
      (acumulado, [clave, valueSeleccion]) => {
        if (clave === claveExcluida) return acumulado;
        if (valueSeleccion.insumoOrdenId?.toString() !== insumoOrdenId?.toString())
          return acumulado;
        return acumulado + (valueSeleccion.cantidad || 0);
      },
      0
    );
    return Math.max(0, (renglon.cantidadAFacturar || 0) - yaLigado);
  }

  cambiarCantidad(entrega, insumo, cantidad) {
    const clave = this.clave(entrega.origen, entrega.recepcionId, insumo.insumoOrdenId);
    this.setState((prevState) => {
      const seleccion = { ...prevState.seleccion };
      if (cantidad === null || cantidad === undefined || cantidad <= 0) {
        delete seleccion[clave];
      } else {
        seleccion[clave] = {
          origen: entrega.origen,
          recepcionId: entrega.recepcionId,
          insumoOrdenId: insumo.insumoOrdenId,
          cantidad,
        };
      }
      return { seleccion };
    });
  }

  // Cuanto pone el check de un renglon: lo que queda sin facturar de esa entrega.
  // Si ya esta todo facturado, pone lo entregado completo en vez de cero, para que
  // marcar siempre haga algo: el N:M esta permitido y la columna "Ya facturada" en
  // naranja mas el resumen del pie ya avisan que se esta duplicando.
  cantidadPorDefecto(insumo) {
    return insumo.cantidadDisponible > 0
      ? insumo.cantidadDisponible
      : insumo.cantidadEntregada || 0;
  }

  // Marca o desmarca varios renglones de una entrega de un golpe. Con todos los
  // insumos de la entrega sirve de "seleccionar toda la recepcion".
  aplicarSeleccion(entrega, insumos, seleccionado) {
    this.setState((prevState) => {
      const seleccion = { ...prevState.seleccion };
      insumos.forEach((valueInsumo) => {
        const clave = this.clave(
          entrega.origen,
          entrega.recepcionId,
          valueInsumo.insumoOrdenId
        );
        if (!seleccionado) {
          delete seleccion[clave];
          return;
        }
        const cantidad = this.cantidadPorDefecto(valueInsumo);
        if (cantidad <= 0) return;
        seleccion[clave] = {
          origen: entrega.origen,
          recepcionId: entrega.recepcionId,
          insumoOrdenId: valueInsumo.insumoOrdenId,
          cantidad,
        };
      });
      return { seleccion };
    });
  }

  // Liga la cantidad completa de todos los renglones de todas las entregas visibles.
  // Son varios setState, pero los updaters funcionales se encadenan sobre el prevState
  // ya actualizado, asi que ninguno pisa al anterior.
  ligarTodasLasEntregas(entregas) {
    entregas.forEach((valueEntrega) =>
      this.aplicarSeleccion(valueEntrega, valueEntrega.insumos, true)
    );
  }

  // true si todos los renglones LIGABLES de la entrega ya estan ligados (para el boton
  // del header). Ignora los que no se pueden ligar (cantidad ausente o cero en datos
  // viejos), o el boton se quedaria para siempre en "Ligar toda" sin hacer nada.
  entregaCompleta(entrega) {
    const ligables = entrega.insumos.filter(
      (valueInsumo) => this.cantidadPorDefecto(valueInsumo) > 0
    );
    return (
      ligables.length > 0 &&
      ligables.every(
        (valueInsumo) =>
          this.state.seleccion[
            this.clave(entrega.origen, entrega.recepcionId, valueInsumo.insumoOrdenId)
          ]
      )
    );
  }

  ligarPendiente(entregas) {
    this.setState((prevState) => {
      const seleccion = { ...prevState.seleccion };
      entregas.forEach((valueEntrega) => {
        valueEntrega.insumos.forEach((valueInsumo) => {
          const clave = this.clave(
            valueEntrega.origen,
            valueEntrega.recepcionId,
            valueInsumo.insumoOrdenId
          );
          if (seleccion[clave]) return;
          // Se calcula contra el estado que se esta construyendo, no contra el del
          // render, para que ligar varias entregas de golpe no sobre-asigne.
          const renglon = this.renglonesPorId()[valueInsumo.insumoOrdenId?.toString()];
          if (!renglon) return;
          const yaLigado = Object.values(seleccion).reduce(
            (acumulado, valueSeleccion) =>
              valueSeleccion.insumoOrdenId?.toString() ===
              valueInsumo.insumoOrdenId?.toString()
                ? acumulado + (valueSeleccion.cantidad || 0)
                : acumulado,
            0
          );
          const pendiente = Math.max(0, (renglon.cantidadAFacturar || 0) - yaLigado);
          const cantidad = Math.min(valueInsumo.cantidadDisponible || 0, pendiente);
          if (cantidad > 0) {
            seleccion[clave] = {
              origen: valueEntrega.origen,
              recepcionId: valueEntrega.recepcionId,
              insumoOrdenId: valueInsumo.insumoOrdenId,
              cantidad,
            };
          }
        });
      });
      return { seleccion };
    });
  }

  guardar() {
    const seleccion = Object.values(this.state.seleccion);
    if (this.props.facturaId) {
      this.setState({ loading: true });
      axios
        .post(
          "ordenesdecompra/relacionarEntregasFactura",
          {
            empresaId: this.props.empresaId,
            facturaId: this.props.facturaId,
            entregasRelacionadas: seleccion,
          },
          { headers: this.props.headersToken }
        )
        .then((res) => {
          this.setState({ loading: false });
          if (res.data.success === 2) {
            message.success(
              "Se relacionaron " + res.data.relacionesGuardadas + " entregas con la factura"
            );
            if (this.props.onConfirm) this.props.onConfirm(this.state.seleccion);
          } else {
            message.error(res.data.message || "No se pudieron guardar las relaciones");
          }
        })
        .catch(() => {
          this.setState({ loading: false });
          message.error("Error al guardar las relaciones");
        });
      return;
    }
    if (this.props.onConfirm) this.props.onConfirm(this.state.seleccion);
  }

  // Resumen del pie: cuanto se esta facturando de cada renglon vs cuanto se amparo.
  // Es un AVISO, no un bloqueo: facturar mercancia aun no recibida es legitimo.
  resumenPorRenglon() {
    // El nombre que trae el padre puede fallar cuando el renglon de la O/C apunta a
    // una relacion con proveedor (Insumo.relaciones[]._id); el del servidor si las
    // resuelve, asi que se usa como respaldo.
    const nombresServidor = {};
    this.state.entregas.forEach((valueEntrega) => {
      valueEntrega.insumos.forEach((valueInsumo) => {
        nombresServidor[valueInsumo.insumoOrdenId?.toString()] = valueInsumo.nombreInsumo;
      });
    });
    return (this.props.renglonesFactura || []).map((valueRenglon) => {
      const amparado = Object.values(this.state.seleccion).reduce(
        (acumulado, valueSeleccion) =>
          valueSeleccion.insumoOrdenId?.toString() ===
          valueRenglon.insumoOrdenId?.toString()
            ? acumulado + (valueSeleccion.cantidad || 0)
            : acumulado,
        0
      );
      return {
        ...valueRenglon,
        nombreInsumo:
          nombresServidor[valueRenglon.insumoOrdenId?.toString()] ||
          valueRenglon.nombreInsumo ||
          "Insumo sin entregas",
        amparado,
      };
    });
  }

  render() {
    const entregas = this.entregasVisibles();
    const resumen = this.resumenPorRenglon();
    const totalLigado = Object.keys(this.state.seleccion).length;

    return (
      <ModalItem
        title="Relacionar Entregas De Material"
        visible={this.props.visible}
        spinning={this.state.loading}
        onCancel={this.props.onCancel}
        onOk={this.guardar.bind(this)}
        okText="Guardar"
        cancelText="Cancelar"
        footer
        paddingTop
        width={window.innerWidth > 1000 ? "75%" : "95%"}
      >
        <Row style={{ marginBottom: 10 }} align="middle">
          <Col span={12}>
            <Space>
              {/* Atajo "inteligente": topa cada renglon contra lo que falta por
                  facturar. Los checks y el boton de cada entrega son literales
                  (ligan toda la cantidad), que es lo que el usuario suele querer
                  cuando marca una recepcion completa a mano. */}
              <ButtonItem
                type="primary"
                blanco
                htmlType="button"
                lessTime
                onClick={() => this.ligarPendiente(entregas)}
              >
                Ligar todo lo pendiente
              </ButtonItem>
              <ButtonItem
                type="primary"
                blanco
                htmlType="button"
                lessTime
                onClick={() => this.ligarTodasLasEntregas(entregas)}
              >
                Ligar todas las entregas
              </ButtonItem>
              {totalLigado > 0 && (
                <>
                  <ButtonItem
                    type="primary"
                    rojo
                    htmlType="button"
                    lessTime
                    onClick={() => this.setState({ seleccion: {} })}
                  >
                    Limpiar
                  </ButtonItem>
                  <Tag color="green">{totalLigado} renglones ligados</Tag>
                </>
              )}
            </Space>
          </Col>
          <Col span={12} style={{ textAlign: "right" }}>
            <Space>
              <Typography.Text style={{ fontSize: "12px", color: "#888" }}>
                Solo insumos de esta factura
              </Typography.Text>
              <Switch
                checked={this.state.soloDeEstaFactura}
                onChange={(checked) => this.setState({ soloDeEstaFactura: checked })}
              />
            </Space>
          </Col>
        </Row>

        {entregas.length === 0 ? (
          <p>
            No se encontro ninguna entrega de material para relacionar con esta factura.
          </p>
        ) : (
          <Collapse style={{ marginBottom: 10 }}>
            {entregas.map((valueEntrega) => {
              const claveEntrega =
                valueEntrega.origen + "|" + valueEntrega.recepcionId;
              const ligadosEntrega = valueEntrega.insumos.filter(
                (valueInsumo) =>
                  this.state.seleccion[
                    this.clave(
                      valueEntrega.origen,
                      valueEntrega.recepcionId,
                      valueInsumo.insumoOrdenId
                    )
                  ]
              ).length;
              return (
                <Panel
                  key={claveEntrega}
                  header={
                    moment(valueEntrega.fecha).locale("es").format("ll") +
                    (valueEntrega.codigoRemision
                      ? " - " + valueEntrega.codigoRemision
                      : "") +
                    (this.props.ordenesDeCompraIds?.length > 1 && valueEntrega.folioOrden
                      ? " (O/C " + valueEntrega.folioOrden + ")"
                      : "")
                  }
                  extra={
                    <Space>
                      {valueEntrega.estadoRecepcion === 0 && (
                        <Tag color="red">Cancelada</Tag>
                      )}
                      {valueEntrega.estadoRecepcion === 2 && (
                        <Tag color="orange">Parcialmente cancelada</Tag>
                      )}
                      {ligadosEntrega > 0 && (
                        <Tag color="green">{ligadosEntrega} ligados</Tag>
                      )}
                      {/* Liga la recepcion completa sin tener que expandirla.
                          stopPropagation o el clic tambien abre/cierra el panel. */}
                      <ButtonItem
                        size="small"
                        htmlType="button"
                        lessTime
                        blanco={!this.entregaCompleta(valueEntrega)}
                        rojo={this.entregaCompleta(valueEntrega)}
                        onClick={(e) => {
                          e.stopPropagation();
                          this.aplicarSeleccion(
                            valueEntrega,
                            valueEntrega.insumos,
                            !this.entregaCompleta(valueEntrega)
                          );
                        }}
                      >
                        {this.entregaCompleta(valueEntrega)
                          ? "Quitar toda"
                          : "Ligar toda"}
                      </ButtonItem>
                    </Space>
                  }
                >
                  <Table
                    size="small"
                    pagination={false}
                    rowKey={(record) => record.insumoOrdenId}
                    dataSource={valueEntrega.insumos}
                    rowSelection={{
                      selectedRowKeys: valueEntrega.insumos
                        .filter(
                          (valueInsumo) =>
                            this.state.seleccion[
                              this.clave(
                                valueEntrega.origen,
                                valueEntrega.recepcionId,
                                valueInsumo.insumoOrdenId
                              )
                            ]
                        )
                        .map((valueInsumo) => valueInsumo.insumoOrdenId),
                      // El check de la fila liga TODO ese insumo; el del encabezado de
                      // la tabla liga la recepcion completa.
                      onSelect: (record, seleccionado) =>
                        this.aplicarSeleccion(valueEntrega, [record], seleccionado),
                      onSelectAll: (seleccionado) =>
                        this.aplicarSeleccion(
                          valueEntrega,
                          valueEntrega.insumos,
                          seleccionado
                        ),
                    }}
                    columns={[
                      {
                        title: "Insumo",
                        dataIndex: "nombreInsumo",
                        key: "nombreInsumo",
                      },
                      {
                        title: "Cant. entregada",
                        dataIndex: "cantidadEntregada",
                        key: "cantidadEntregada",
                        align: "right",
                        width: 130,
                      },
                      {
                        title: "Ya facturada",
                        dataIndex: "cantidadYaAmparada",
                        key: "cantidadYaAmparada",
                        align: "right",
                        width: 130,
                        render: (valor) =>
                          valor > 0 ? <Tag color="orange">{valor}</Tag> : 0,
                      },
                      {
                        title: "Disponible",
                        dataIndex: "cantidadDisponible",
                        key: "cantidadDisponible",
                        align: "right",
                        width: 120,
                      },
                      {
                        title: "Cant. a amparar",
                        key: "cantidadAAmparar",
                        align: "right",
                        width: 160,
                        render: (valor, record) => {
                          const clave = this.clave(
                            valueEntrega.origen,
                            valueEntrega.recepcionId,
                            record.insumoOrdenId
                          );
                          return (
                            <InputNumberItem
                              value={this.state.seleccion[clave]?.cantidad}
                              min={0}
                              max={record.cantidadEntregada}
                              placeholder="0"
                              onChange={(cantidad) =>
                                this.cambiarCantidad(valueEntrega, record, cantidad)
                              }
                            />
                          );
                        },
                      },
                    ]}
                  />
                </Panel>
              );
            })}
          </Collapse>
        )}

        {resumen.length > 0 && (
          <Table
            size="small"
            pagination={false}
            title={() => "Resumen por insumo de la factura"}
            rowKey={(record) => record.insumoOrdenId}
            dataSource={resumen}
            columns={[
              { title: "Insumo", dataIndex: "nombreInsumo", key: "nombreInsumo" },
              {
                title: "Cant. a facturar",
                dataIndex: "cantidadAFacturar",
                key: "cantidadAFacturar",
                align: "right",
                width: 150,
              },
              {
                title: "Total amparado",
                dataIndex: "amparado",
                key: "amparado",
                align: "right",
                width: 150,
                render: (valor, record) =>
                  valor !== record.cantidadAFacturar ? (
                    <Typography.Text style={{ color: "#FA8C16" }}>{valor}</Typography.Text>
                  ) : (
                    valor
                  ),
              },
            ]}
          />
        )}
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(RelacionarEntregasModal);
